import { useCallback, useState, useEffect, useRef } from "react";
import {
  getLessonEventsClient,
  getSandboxClient,
  ServiceError,
  ReplParam,
  type SandboxStatus,
} from "sj-ui-toolkit/src/client/svcClients";

import { usePolling } from "sj-ui-toolkit/src/hooks";

import { ClientSessionState, ClientStatus, TimeoutStatus } from "../SandboxContext";

export interface SessionTimeoutConfig {
  timeoutMinutes: number;
}

export interface UseClaimResult {
  session: ClientSessionState;

  requestSandbox: (engine?: string) => Promise<void>;
  resetSandbox: (engine?: string) => Promise<void>;
  abandonSandbox: () => Promise<void>;
}

/**
 * Hook to manage sandbox claiming and resetting.
 * Handles requesting a new sandbox and resetting an existing sandbox.
 * Also tracks session time and manages session expiration.
 */
export const useClaimInternal = (
  lessonKey: string,
  engines: string[],
  timeoutConfig?: SessionTimeoutConfig
): UseClaimResult => {
  // API clients
  const sandboxClient = getSandboxClient();
  const eventsClient = getLessonEventsClient();

  const [clientStatus, setClientStatus] = useState<ClientStatus>("initial");
  const [sandboxStatus, setSandboxStatus] = useState<SandboxStatus | null>(null);
  const [claimError, setClaimError] = useState<ServiceError | Error | null>(null);
  const [replParam, setReplParam] = useState<ReplParam | undefined>(undefined);
  const [timeoutStatus, setTimeoutStatus] = useState<TimeoutStatus>(undefined);

  // Time tracking refs
  const readyTimestampRef = useRef<Date | null>(null);
  const totalSecondsActiveRef = useRef(0);
  const lastActivityTimeRef = useRef(Date.now());
  const lastReportTimeRef = useRef(Date.now());
  const [totalSecondsActive, setTotalSecondsActive] = useState(0);

  /**
   * When the sandboxStatus changes, update clientStatus accordingly
   */
  useEffect(() => {
    if (claimError) {
      setClientStatus("error");
    } else if (sandboxStatus) {
      if (sandboxStatus.status === "Running") {
        setClientStatus("ready");
        readyTimestampRef.current = new Date();
      } else {
        setClientStatus("acquiring");
        readyTimestampRef.current = null;
        setClaimError(null);
      }
    }
  }, [sandboxStatus, claimError]);

  /**
   * Helper method to reset sandbox state when requesting or resetting a sandbox
   */
  function resetHookState() {
    setClientStatus("resetting");
    setSandboxStatus(null);
    setClaimError(null);
    readyTimestampRef.current = null;
  }

  /**
   * Calculate elapsed time in seconds
   */
  const getElapsedSeconds = () => {
    if (!readyTimestampRef.current) return 0;
    return Math.floor((Date.now() - readyTimestampRef.current.getTime()) / 1000);
  };

  // Mark timeout status as "active" when user interacts with the page
  const recordUserActivity = useCallback(() => {
    lastActivityTimeRef.current = Date.now();
    // Reset timeout status to active
    setTimeoutStatus("active");
  }, []);

  // Let the sandbox know when the user is active or leaving (called every minute for active)
  const reportActivityImpl = useCallback(
    async (isLeaving: boolean) => {
      if (readyTimestampRef.current) {
        await eventsClient.recordActiveEvent(
          lessonKey,
          readyTimestampRef.current,
          getElapsedSeconds(),
          isLeaving
        ).promise;
      }
    },
    [lessonKey]
  );

  // Generate and set the replParam
  function engineToReplParam(engine?: string) {
    let param: ReplParam | undefined = undefined;
    if (engine && engines?.length > 0) {
      if (!engines.includes(engine)) {
        console.warn(
          `Requested engine "${engine}" is not in the list of available engines: [${engines.join(
            ", "
          )}]. Using default engine instead.`
        );
        engine = engines[0];
      }

      param = { "repl-engine": engine };
    }

    // Save the replParam in state
    setReplParam(param);

    return param;
  }

  /**
   * Set up activity listeners
   */
  useEffect(() => {
    // Don't set up listeners if timeout has expired
    if (timeoutStatus === "expired") {
      return;
    }

    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart", "focus", "blur"];

    activityEvents.forEach(event => {
      window.addEventListener(event, recordUserActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, recordUserActivity);
      });
    };
  }, [recordUserActivity, timeoutStatus]);

  /**
   * Set up periodic activity reporting and timeout checking, only when sandbox is ready
   */
  useEffect(() => {
    if (clientStatus !== "ready") {
      return;
    }

    const reportIntervalId = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityTimeRef.current;
      const timeSinceLastReport = now - lastReportTimeRef.current;
      const oneMinuteInMs = 60 * 1000;

      // Report if user has been active within the last minute
      // This ensures we report every minute as long as the user is active
      if (timeSinceLastActivity < oneMinuteInMs && timeSinceLastReport >= oneMinuteInMs) {
        lastReportTimeRef.current = now;
        reportActivityImpl(false);
      }
    }, 60 * 1000); // Report every 60 seconds

    const timeoutIntervalId = setInterval(() => {
      const elapsedSeconds = getElapsedSeconds();
      totalSecondsActiveRef.current = elapsedSeconds;
      setTotalSecondsActive(elapsedSeconds);

      if (timeoutConfig) {
        const now = Date.now();
        const timeSinceLastActivity = now - lastActivityTimeRef.current;
        const timeoutMs = timeoutConfig.timeoutMinutes * 60 * 1000;
        const warningThresholdMs = (timeoutConfig.timeoutMinutes - 1) * 60 * 1000;

        // Set status to "expired" if we've reached the timeout threshold
        if (timeSinceLastActivity >= timeoutMs) {
          setTimeoutStatus("expired");
        }
        // Set status to "expiring" if we've crossed the warning threshold but not expired yet
        else if (timeSinceLastActivity >= warningThresholdMs) {
          setTimeoutStatus("expiring");
        }
      }
    }, 1000); // Check timeout every 1 second for tight sync with countdown

    return () => {
      clearInterval(reportIntervalId);
      clearInterval(timeoutIntervalId);
    };
  }, [timeoutConfig, clientStatus, reportActivityImpl]);

  /**
   * Callback to poll for sandbox readiness. This gets re-created when lessonKey or clientStatus changes.
   */
  const pollForSandboxReady = useCallback(async () => {
    if (!lessonKey || clientStatus !== "acquiring") return;

    sandboxClient
      .getStatus()
      .promise.then(async s => {
        setSandboxStatus(s);
      })
      .catch(async (error: Error) => {
        setClaimError(error);
      });
  }, [lessonKey, clientStatus, sandboxClient]);

  // Use polling hook to check status every 3 seconds when in polling state
  usePolling(pollForSandboxReady, {
    interval: 3000,
    enabled: clientStatus === "acquiring",
    runOnMount: true,
  });

  /**
   * Request a new sandbox
   */
  const requestSandbox = useCallback(
    async (engine?: string) => {
      resetHookState();

      const param = engineToReplParam(engine);

      // Using promise chaining to avoid messy try/catch nesting
      sandboxClient
        .acquire(lessonKey, 1, undefined, param)
        .promise.catch((error: unknown) => {
          if (error instanceof ServiceError && error.status === 409) {
            // 409 Conflict - user has a sandbox but it's the wrong one. Call reset to get the right one.
            return sandboxClient.reset(lessonKey, 1, undefined, param).promise;
          }

          // Rethrow other errors to be caught in the outer catch
          throw error;
        })
        .then(s => {
          // If we get here, then we have a valid sandbox state from acquire/reset
          setSandboxStatus(s);
        })
        .catch((error: Error) => {
          setClaimError(error);
        });
    },
    [sandboxClient, lessonKey]
  );

  /**
   * Reset the current sandbox
   */
  const resetSandbox = useCallback(
    async (engine?: string) => {
      if (clientStatus !== "ready") {
        throw new Error(
          `Cannot reset sandbox: clientStatus is "${clientStatus}", expected "ready"`
        );
      }

      // Reset hook to initial state
      resetHookState();

      const resetReplParam = engine ? engineToReplParam(engine) : replParam;

      sandboxClient
        .reset(lessonKey, 1, undefined, resetReplParam)
        .promise.then(s => {
          // If we get here, then we have a valid sandbox state from acquire/reset
          setSandboxStatus(s);
        })
        .catch((error: Error) => {
          setClaimError(error);
        });
    },
    [sandboxClient, replParam, lessonKey, clientStatus]
  );

  /**
   * Abandon the current sandbox when leaving the lesson
   */
  const abandonSandbox = useCallback(async () => {
    await reportActivityImpl(true);
  }, [reportActivityImpl]);

  return {
    session: {
      overall: clientStatus,
      timeout: timeoutStatus,
      sandbox: sandboxStatus,
      error: claimError,
      readyTimestamp: readyTimestampRef.current,
      totalSecondsActive,
      replParam: replParam,
    },

    requestSandbox,
    resetSandbox,
    abandonSandbox,
  };
};
